/**
 * Proving control of the mobile number (`PHONE_VERIFIED`).
 *
 * `awaiting_details` already COLLECTED a phone number. That is a claim. This
 * screen turns it into a proof by texting a code to it and asking for the code
 * back, so the number stops being something the applicant typed and becomes
 * something they demonstrably hold.
 *
 * Three rules here follow from the backend rather than from taste:
 *
 *  * **The number is typed again, not prefilled.** The collected copy is sealed
 *    per-row on the server and is deliberately not readable from an applicant
 *    token. Re-entering it is also the honest shape of the step: this is a
 *    confirmation, and a prefilled field confirms nothing.
 *  * **A wrong code is a 200.** `verifyPhoneOtp` resolves with
 *    `verified: false`; only a request that could not be answered throws. Code
 *    that treats "no exception" as success passes every wrong guess.
 *  * **Each send spends an SMS.** Nothing here sends speculatively, on mount,
 *    or on a retry loop — every send is an explicit act by the applicant.
 *
 * `PHONE_VERIFIED` is fired only after the server said so, and the server
 * re-checks its own evidence when it arrives, so a client that fired it anyway
 * gets a 422 rather than a free pass.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CollectionFlowError,
  postCollectionFlowEvent,
  resendPhoneOtp,
  sendPhoneOtp,
  verifyPhoneOtp,
} from './client';
import {
  isTerminalVerdict,
  looksLikeE164,
  MAX_CODE_LEN,
  messageKeyFor,
  verdictKeyFor,
} from './phoneMessages';
import { PhoneInput } from './PhoneInput';
import type { CollectionFlowSnapshot, PhoneOtpSent, PhoneOtpVerdict } from './types';

export const PHONE_VERIFIED_EVENT = 'PHONE_VERIFIED';

/**
 * Everything the step does outside itself: the three OTP calls and the event.
 * A case binds them to the applicant's session; the console's flow preview
 * supplies its own, which texts nobody.
 */
export interface PhoneOtp {
  send: (phone: string) => Promise<PhoneOtpSent>;
  resend: () => Promise<PhoneOtpSent>;
  verify: (code: string) => Promise<PhoneOtpVerdict>;
  /** Fire `PHONE_VERIFIED` and move on. */
  advance: () => Promise<void>;
}

function sessionOtp(
  token: string,
  onAdvance: (snapshot: CollectionFlowSnapshot) => void,
): PhoneOtp {
  return {
    send: (phone) => sendPhoneOtp(token, phone),
    resend: () => resendPhoneOtp(token),
    verify: (code) => verifyPhoneOtp(token, code),
    advance: async () => onAdvance(await postCollectionFlowEvent(token, PHONE_VERIFIED_EVENT)),
  };
}

interface PhoneStepProps {
  token: string;
  /** Receives the next snapshot once the flow advances. */
  onAdvance: (snapshot: CollectionFlowSnapshot) => void;
  /** In place of the session's calls — the console's flow preview. Absent on a case. */
  otp?: PhoneOtp;
}

export function PhoneStep({ token, onAdvance, otp }: PhoneStepProps) {
  const { t } = useTranslation();
  const calls = otp ?? sessionOtp(token, onAdvance);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  /** Set once a code is in flight — this is what switches the screen. */
  const [sent, setSent] = useState<{ ref: string; masked: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  /** Fire the statechart event and hand the next snapshot up. */
  const advance = async () => {
    try {
      await calls.advance();
    } catch (error) {
      setErrorKey(messageKeyFor(error));
    }
  };

  const send = async () => {
    setBusy(true);
    setErrorKey(null);
    try {
      const result = await calls.send(phone);
      setSent({ ref: result.ref_code, masked: result.phone_masked });
      setCode('');
    } catch (error) {
      // The server already holds a verified row for this case: the step is
      // done and only this screen disagrees. Advancing is the honest
      // resolution — telling the applicant to "continue" on a screen whose
      // only button buys another SMS is not.
      if (error instanceof CollectionFlowError && error.code === 'already_verified') {
        await advance();
        return;
      }
      setErrorKey(messageKeyFor(error));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    setErrorKey(null);
    try {
      const result = await calls.resend();
      setSent({ ref: result.ref_code, masked: result.phone_masked });
      setCode('');
    } catch (error) {
      setErrorKey(messageKeyFor(error));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setErrorKey(null);
    try {
      const verdict = await calls.verify(code);
      if (!verdict.verified) {
        setErrorKey(verdictKeyFor(verdict.status));
        // The session is gone, not merely unsatisfied. Staying here would
        // leave a live Confirm button over a dead session and a resend that
        // can only fail — so go back and ask for a new code, which is what
        // the message already says to do.
        if (isTerminalVerdict(verdict.status)) setSent(null);
        return;
      }
      await advance();
    } catch (error) {
      setErrorKey(messageKeyFor(error));
    } finally {
      setBusy(false);
    }
  };

  // `alert` + role="alert" is what SignatureStep uses; screen readers announce
  // it without the applicant having to find it.
  const error = errorKey ? (
    <p className="alert" role="alert" data-testid="phone-error">
      {t(errorKey)}
    </p>
  ) : null;

  if (!sent) {
    return (
      <>
        <h1 className="step-title">{t('flow.phone.title')}</h1>
        <p className="step-text">{t('flow.phone.intro')}</p>
        <form
          className="details-form"
          onSubmit={(event) => {
            event.preventDefault();
            void send();
          }}
        >
          <div className="details-field">
            <span className="details-label" aria-hidden="true">
              {t('flow.phone.numberLabel')}
            </span>
            {/* Emits E.164, which `send` requires — the applicant types the
                number the way they write it and picks the country. */}
            <PhoneInput
              value={phone}
              onChange={setPhone}
              label={t('flow.phone.numberLabel')}
              placeholder={t('flow.phone.numberPlaceholder')}
              disabled={busy}
            />
          </div>
          {error}
          <div className="actions">
            <button
              type="submit"
              className="btn"
              disabled={busy || !looksLikeE164(phone)}
              data-track="send_code"
            >
              {busy ? t('flow.phone.sending') : t('flow.phone.send')}
            </button>
          </div>
        </form>
      </>
    );
  }

  return (
    <>
      <h1 className="step-title">{t('flow.phone.codeTitle')}</h1>
      <p className="step-text">{t('flow.phone.sentTo', { phone: sent.masked })}</p>
      {/* The reference is how an applicant tells this SMS apart from an older
          one still sitting in their inbox. */}
      <p className="step-text">{t('flow.phone.ref', { ref: sent.ref })}</p>
      <form
        className="details-form"
        onSubmit={(event) => {
          event.preventDefault();
          void confirm();
        }}
      >
        <label className="details-field">
          <span className="details-label">{t('flow.phone.codeLabel')}</span>
          <input
            className="details-input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={MAX_CODE_LEN}
            value={code}
            onChange={(event) => setCode(event.target.value)}
          />
        </label>
        {error}
        <div className="actions">
          <button type="submit" className="btn" disabled={busy || code.trim().length === 0} data-track="confirm_code">
            {busy ? t('flow.phone.checking') : t('flow.phone.confirm')}
          </button>
          <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => void resend()} data-track="resend_code">
            {t('flow.phone.resend')}
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            disabled={busy}
            data-track="change_number"
            onClick={() => {
              setSent(null);
              setErrorKey(null);
            }}
          >
            {t('flow.phone.changeNumber')}
          </button>
        </div>
      </form>
    </>
  );
}
